<template>
  <div class="ppv-filters-card">
    <PpvFilterPanelHeader
      :open="open"
      :active-filter-count="computedActiveFilterCount"
      :active-date-label="computedActiveDateLabel"
      :active-tracks="filters.tracks"
      :active-triages="filters.triages"
      :condition-mode="filters.conditionMode || 'user_selected'"
      :get-track-label="getTrackLabel"
      :get-triage-label="getTriageLabel"
      @toggle="toggleOpen"
      @clear="clearAllFilters"
    />

    <div v-show="open" class="ppv-filters-body">
      <PpvDateFilter
        :selected-range="filters.dateRange"
        :date-ranges="dateRanges"
        :on-select="selectDateRange"
      />
      <PpvConditionsFilter
        :selected-mode="filters.conditionMode || 'user_selected'"
        :on-select="selectConditionMode"
      />
      <PpvTrackFilter
        :selected-tracks="filters.tracks"
        :track-options="trackOptions"
        :on-toggle="toggleTrack"
      />
      <PpvTriageFilter
        :selected-triages="filters.triages"
        :triage-options="triageOptions"
        :on-toggle="toggleTriage"
      />
      <PpvDiseaseFilter
        :selected-diseases="filters.diseaseCategories"
        :active-disease-categories="computedActiveDiseaseCategories"
        :inactive-disease-categories="computedInactiveDiseaseCategories"
        :on-toggle="toggleDisease"
      />
      <PpvDateRangeModal
        :show="dateModalOpen"
        :date-from="filters.dateFrom"
        :date-to="filters.dateTo"
        @close="dateModalOpen = false"
        @apply="applyDateRange"
      />
      
      <!-- TODO: Re-enable when the online data pull is available. -->
      <!-- <PpvOlderVisitsModal
        :show="olderVisitsModalOpen"
        @close="olderVisitsModalOpen = false"
        @search="handleOlderSearch"
      /> -->
    </div>
  </div>
</template>

<script>
import { toRefs } from 'vue'
import icons from '@/mixins/icons'
import { EVENTS, PVI_PANEL_NAME } from '@/plugins/analytics/events'
import { createThrottledTracker, calcRangeDays } from '@/plugins/analytics/helpers'
import { useFilterPanel } from './composables/useFilterPanel'
import PpvFilterPanelHeader from './filters/PpvFilterPanelHeader.vue'
import PpvDateFilter from './filters/PpvDateFilter'
import PpvConditionsFilter from './filters/PpvConditionsFilter'
import PpvTrackFilter from './filters/PpvTrackFilter'
import PpvTriageFilter from './filters/PpvTriageFilter'
import PpvDiseaseFilter from './filters/PpvDiseaseFilter'
import PpvDateRangeModal from './PpvDateRangeModal'
import PpvOlderVisitsModal from './PpvOlderVisitsModal'

export default {
  name: 'PpvFilterPanel',
  components: {
    PpvFilterPanelHeader,
    PpvDateFilter,
    PpvConditionsFilter,
    PpvTrackFilter,
    PpvTriageFilter,
    PpvDiseaseFilter,
    PpvDateRangeModal,
    PpvOlderVisitsModal,
  },
  mixins: [icons],
  props: {
    filters: {
      type: Object,
      required: true
    },
    visits: {
      type: Array,
      required: true
    },
    allDiseaseCategories: {
      type: Array,
      default: () => []
    },
    dateRanges: {
      type: Array,
      default: () => []
    },
    trackOptions: {
      type: Array,
      default: () => []
    },
    trackMap: {
      type: Object,
      default: () => ({})
    },
    triageOptions: {
      type: Array,
      default: () => []
    },
    allVisits: {
      type: Array,
      default: () => []
    },
  },
  setup(props) {
    const { filters, visits, allDiseaseCategories, trackMap } = toRefs(props)
    const panel = useFilterPanel(filters, visits, allDiseaseCategories, trackMap)

    return { panel }
  },
  data() {
    return {
      open: false,
      dateModalOpen: false,
      olderVisitsModalOpen: false,
    }
  },
  created() {
    this.throttledTrack = createThrottledTracker(
      (event, props) => this.$track(event, props),
      500
    )
  },
  computed: {
    computedActiveFilterCount() {
      return this.panel.activeFilterCount()
    },
    computedActiveDateLabel() {
      return this.$t(this.panel.activeDateLabel())
    },
    computedActiveDiseaseCategories() {
      return this.panel.activeDiseaseCategories()
    },
    computedInactiveDiseaseCategories() {
      return this.panel.inactiveDiseaseCategories()
    },
    getTrackLabel() {
      return this.panel.getTrackLabel
    },
    getTriageLabel() {
      return this.panel.getTriageLabel
    },
  },
  methods: {
    toggleOpen() {
      this.open = !this.open
    },
    updateFilters(patch) {
      this.$emit('update:filters', { ...this.filters, ...patch })
    },
    clearAllFilters() {
      this.$emit('update:filters', {
        dateRange: 'month',
        dateFrom: '',
        dateTo: '',
        conditionMode: 'user_selected',
        tracks: [],
        triages: [],
        diseaseCategories: [],
      })
      this.$track(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'clear_all',
        filter_value: 'reset',
        panel_name: PVI_PANEL_NAME,
      })
    },
    selectDateRange(range) {
      if (range === 'specific') {
        this.dateModalOpen = true

        return
      }

      if (range === 'older') {
        this.olderVisitsModalOpen = true

        return
      }
      
      this.throttledTrack(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'date',
        filter_value: range,
        panel_name: PVI_PANEL_NAME,
      })
      this.updateFilters({ dateRange: range })
    },
    applyDateRange({ from, to }) {
      this.updateFilters({
        dateFrom: from,
        dateTo: to,
        dateRange: 'specific',
      })

      const rangeDays = calcRangeDays(from, to)
      this.$track(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'date_custom',
        filter_value: 'specific',
        range_length_days: rangeDays,
        panel_name: PVI_PANEL_NAME,
      })
      this.dateModalOpen = false
    },
    handleOlderSearch(results) {
      this.olderVisitsModalOpen = false
      this.updateFilters({ dateRange: 'older' })
      const existingIds = new Set(this.allVisits.map(v => v.id))
      const newVisits = results.filter(v => !existingIds.has(v.id))

      if (newVisits.length) {
        this.$emit('update:all-visits', [...this.allVisits, ...newVisits])
      }
    },
    selectConditionMode(mode) {
      this.updateFilters({ conditionMode: mode })
    },
    toggleTrack(value) {
      this.throttledTrack(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'track',
        filter_value: value,
        panel_name: PVI_PANEL_NAME,
      })
      this.toggleFilter('tracks', value)
    },
    toggleTriage(value) {
      this.throttledTrack(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'triage',
        filter_value: value,
        panel_name: PVI_PANEL_NAME,
      })
      this.toggleFilter('triages', value)
    },
    toggleDisease(value) {
      this.throttledTrack(EVENTS.PV_FILTER_CHANGED, {
        filter_type: 'disease',
        filter_value: value,
        panel_name: PVI_PANEL_NAME,
      })
      this.toggleFilter('diseaseCategories', value)
    },
    toggleFilter(key, val) {
      const arr = [...this.filters[key]]
      const i = arr.indexOf(val)

      if (i === -1) {
        arr.push(val)
      } else {
        arr.splice(i, 1)
      }
      this.updateFilters({ [key]: arr })
    }
  }
}
</script>

<style scoped>
.ppv-filters-card {
  --chip-radius: 20px;
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: 1px solid #eaedf2;
}
.ppv-filters-body {
  padding: 8px 20px 24px;
  max-height: 70vh;
  overflow: auto;
}
</style>
